import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import CMSGallery from "../components/CMSGallery";
import { COMPANY, IMAGES, BENEFITS, SERVICES, FAQS, FORSIDE_TEXT } from "../data/content";

export default function Forside() {
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);

  const toggleFaq = (idx) => {
    setOpenFaqIndex(openFaqIndex === idx ? -1 : idx);
  };

  return (
    <>
      <SEO
        title="Forside"
        description="Certificeret ISNHCP hovtrimmer Metteline Hjorth tilbyder skånsom &amp; naturlig hovtrimning i Lemvig, Struer og hele Jylland. Få sunde, stærke hove."
      />

      {/* HERO SECTION */}
      <section className="relative bg-forest-950 text-white overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero.replace("assets.auxodev.dk/", "assets.auxodev.dk/cdn-cgi/image/width=1800,format=auto,quality=85/")}
            alt="Hest i naturlige danske omgivelser i Jylland"
            className="w-full h-full object-cover object-center opacity-65 filter brightness-[0.95]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-forest-950/70 via-forest-950/40 to-forest-950/15"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-forest-900/80 border border-forest-700/60 text-xs font-semibold text-emerald-300">
              <i className="fa-solid fa-location-dot text-amber-400"></i>
              <span>{COMPANY.tagline}</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-sand-50 leading-[1.15]">
              Certificeret hovtrimmer <br />&amp; naturlig hovtrimning
            </h1>

            <p className="text-base sm:text-lg text-sand-200 leading-relaxed font-normal">
              {FORSIDE_TEXT.introParagraph1}
            </p>

            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Link
                to="/kontakt"
                className="px-7 py-4 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-bold text-base transition-colors shadow-lg cursor-pointer text-center flex items-center justify-center gap-2.5"
              >
                <i className="fa-solid fa-calendar-check text-emerald-300"></i>
                <span>Kontakt mig</span>
              </Link>
              <Link
                to="/hovtrim"
                className="px-7 py-4 rounded-xl bg-sand-100/10 hover:bg-sand-100/20 text-sand-100 border border-sand-100/30 font-semibold text-base transition-colors cursor-pointer text-center flex items-center justify-center gap-2"
              >
                <span>Læs om naturlig hovtrim</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>

            <div className="pt-6 border-t border-forest-800/80 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-sand-200">
              <span className="flex items-center gap-1.5">
                <i className="fa-solid fa-check text-emerald-400"></i> Jaime Jacksons model
              </span>
              <span className="flex items-center gap-1.5">
                <i className="fa-solid fa-check text-emerald-400"></i> Fabjergvej 142, Lemvig
              </span>
              <span className="flex items-center gap-1.5">
                <i className="fa-solid fa-check text-emerald-400"></i> Kører i hele Jylland
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* DETALJERET TEKST FRA SCRAPE - SPLIT LAYOUT MED OVERLAPPING CARDS */}
      <section className="py-16 lg:py-24 bg-sand-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            {/* Left Column: Text Hierarchy, Pill, Large Heading, Supporting Text, Stat & CTA */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-forest-100/80 border border-forest-200 text-xs font-semibold text-forest-900">
                <i className="fa-solid fa-seedling text-emerald-700"></i>
                <span>Naturlig hovtrimning</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-forest-900 tracking-tight leading-[1.18]">
                {FORSIDE_TEXT.heading}
              </h2>

              <p className="text-base sm:text-lg text-charcoal-700 leading-relaxed font-normal">
                {FORSIDE_TEXT.introParagraph1}
              </p>

              {/* Stat Figure */}
              <div className="flex items-center gap-4 py-3 border-y border-sand-200">
                <span className="text-5xl sm:text-6xl font-extrabold text-forest-800 tracking-tight">
                  100%
                </span>
                <div>
                  <span className="block text-sm font-bold text-forest-950 uppercase tracking-wide">
                    Naturlig hovbalance
                  </span>
                  <span className="text-xs text-charcoal-600">
                    Trimmer efter vilde hestes naturlige slid i Nevadas ørken
                  </span>
                </div>
              </div>

              <p className="text-sm sm:text-base text-charcoal-700 leading-relaxed">
                {FORSIDE_TEXT.paragraph2}
              </p>

              <p className="text-sm sm:text-base text-charcoal-700 leading-relaxed">
                {FORSIDE_TEXT.paragraph3}
              </p>

              <p className="text-sm sm:text-base text-charcoal-700 leading-relaxed">
                {FORSIDE_TEXT.paragraph4}
              </p>

              {/* CTA Button */}
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-sm shadow-md transition-colors cursor-pointer"
                >
                  <i className="fa-solid fa-calendar-check text-emerald-400"></i>
                  <span>Kontakt mig for en aftale</span>
                </Link>
              </div>
            </div>

            {/* Right Column: Prominent Image */}
            <div className="lg:col-span-5 relative">
              <div className="relative overflow-hidden rounded-3xl border border-sand-300 shadow-xl">
                <img
                  src={IMAGES.portrait.replace("assets.auxodev.dk/", "assets.auxodev.dk/cdn-cgi/image/width=1000,format=auto,quality=85/")}
                  alt="Hoofcare Metteline Hjorth hovtrimmer"
                  className="w-full h-[420px] sm:h-[480px] lg:h-[520px] object-cover object-top"
                />
                <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-forest-950/90 to-transparent text-sand-50 text-xs flex items-center justify-between">
                  <span className="font-medium">Hoofcare v. Metteline Hjorth</span>
                  <span className="font-semibold text-emerald-300 flex items-center gap-1">
                    <i className="fa-solid fa-certificate"></i> Lemvig & Jylland
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Overlapping Feature Card floating across boundary - WIDTH MATCHED TO CONTAINER */}
          <div className="relative z-20 mt-12 lg:mt-16 w-full bg-forest-900 text-sand-50 p-6 sm:p-8 rounded-2xl border border-forest-800 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-start">
              
              {/* Feature Block 1: Guidelines */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-forest-800 text-emerald-400 flex items-center justify-center text-lg font-bold">
                    <i className="fa-solid fa-list-check"></i>
                  </div>
                  <h3 className="font-bold text-white text-base sm:text-lg">
                    {FORSIDE_TEXT.guidelinesTitle}
                  </h3>
                </div>
                <ul className="space-y-1.5 text-xs sm:text-sm text-sand-200/90 leading-relaxed">
                  {FORSIDE_TEXT.guidelines.map((g, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-emerald-400 mt-1 shrink-0 text-xs"></i>
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs italic text-emerald-300 pt-1 font-medium">
                  "{FORSIDE_TEXT.quote}"
                </p>
              </div>

              {/* Feature Block 2: Freereining */}
              <div className="space-y-3 md:border-l md:border-forest-800 md:pl-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-forest-800 text-emerald-400 flex items-center justify-center text-lg font-bold">
                    <i className="fa-solid fa-horse"></i>
                  </div>
                  <h3 className="font-bold text-white text-base sm:text-lg">
                    Freereining Trimning
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-sand-200/90 leading-relaxed">
                  {FORSIDE_TEXT.paragraph5}
                </p>
                <div className="pt-2">
                  <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300 font-semibold bg-forest-800/80 px-3 py-1.5 rounded-lg border border-forest-700/60">
                    <i className="fa-solid fa-shield-halved"></i>
                    Afslappet og tryg oplevelse for hesten
                  </span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* FORDELE VED NATURLIG HOVTRIMNING - 3-COLUMN MINIMALIST GRID WITH VERTICAL DIVIDERS, NO CARDS */}
      <section className="py-20 bg-sand-50 border-y border-sand-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-forest-100/80 border border-forest-200 text-xs font-semibold text-forest-900">
              <i className="fa-solid fa-leaf text-emerald-700"></i>
              <span>Hvorfor vælge naturlig hovtrimning?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-forest-900 tracking-tight">
              Fordele ved naturlig hovtrimning
            </h2>
            <p className="text-charcoal-700 text-sm sm:text-base leading-relaxed">
              Når hoven fungerer som naturen har designet den, opnår hesten bedre komfort og langvarig sundhed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-0 relative">
            {BENEFITS.slice(0, 3).map((b, idx) => (
              <div
                key={idx}
                className={`flex flex-col items-center text-center px-4 sm:px-8 space-y-4 ${
                  idx !== 2 ? "md:border-r md:border-sand-300 pb-8 md:pb-0 border-b md:border-b-0 border-sand-200" : ""
                }`}
              >
                {/* Thin outline icon directly on background */}
                <i className={`${b.icon.replace('fa-solid', 'fa-regular')} text-3xl sm:text-4xl text-forest-800`}></i>
                
                <h3 className="text-xl font-bold text-forest-900 tracking-tight">
                  {b.title}
                </h3>
                
                <p className="text-sm text-charcoal-700 leading-relaxed max-w-sm">
                  {b.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OM HOOFCARE - 2-COLUMN SPLIT LAYOUT WITH IMAGE ON LEFT SIDE ON DESKTOP */}
      <section className="py-16 sm:py-24 bg-sand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
            
            {/* Left Column: Image on Left Side on Desktop (height matching container) */}
            <div className="lg:col-span-6 order-2 lg:order-1 h-full">
              <div className="relative rounded-3xl overflow-hidden border border-sand-300 shadow-xl h-full flex flex-col">
                <img
                  src={IMAGES.trimming.replace("assets.auxodev.dk/", "assets.auxodev.dk/cdn-cgi/image/width=1000,format=auto,quality=85/")}
                  alt="Naturlig hovtrimning i praksis i stald"
                  className="w-full h-full min-h-[360px] sm:min-h-[480px] object-cover"
                />
              </div>
            </div>

            {/* Right Column: Text Content */}
            <div className="lg:col-span-6 space-y-6 order-1 lg:order-2 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-forest-100/80 border border-forest-200 text-xs font-semibold text-forest-900 self-start">
                <i className="fa-solid fa-horse text-emerald-700"></i>
                <span>Om virksomheden</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-forest-900 tracking-tight leading-tight">
                Om Hoofcare
              </h2>

              <p className="text-charcoal-700 leading-relaxed text-base sm:text-lg font-normal">
                Hoofcare drives med en brændende passion for hestes trivsel og biomekanik. Med udgangspunkt i Fabjergvej 142 i Lemvig kører jeg ud i hele Jylland for at rådgive og udføre professionel naturlig hovtrimning.
              </p>

              <div className="space-y-3 pt-2">
                <h3 className="text-base font-bold text-forest-900">
                  Hvorfor vælge Hoofcare?
                </h3>
                <ul className="space-y-3 text-sm sm:text-base text-charcoal-700">
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i>
                    <span><strong>Certificeret Natural Horse Care Practitioner:</strong> Certificeret i Jaime Jacksons model, instruktør for ISNHCP og De 4 Søjler.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i>
                    <span><strong>Freereining trimning:</strong> Hesten står udbundet under trimningen for maksimal ro og afslapning.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i>
                    <span><strong>Udkørende i hele Jylland:</strong> Fleksibel planlægning og samleruter direkte til din stald.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-check text-emerald-600 mt-1 shrink-0"></i>
                    <span><strong>Helhedsorienteret rådgivning:</strong> Vejledning om foldmiljø, vandrefolde, ernæring og hesteadfærd.</span>
                  </li>
                </ul>
              </div>

              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-sm shadow-md transition-colors cursor-pointer"
                >
                  <i className="fa-solid fa-envelope text-emerald-400"></i>
                  <span>Book tid til din hest</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* UDVALGTE YDELSER - HEADER + LEFT BORDER FEATURE GRID (NO ICONS, NO CARDS) */}
      <section className="py-20 bg-sand-100/60 border-t border-sand-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header Block with Eyebrow, Main Heading & Rule */}
          <div className="mb-14 space-y-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-forest-100/80 border border-forest-200 text-xs font-semibold text-forest-900">
              <i className="fa-solid fa-briefcase text-emerald-700"></i>
              <span>Hvad tilbyder jeg?</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-forest-900 tracking-tight">
                Udvalgte Ydelser
              </h2>
              <Link
                to="/priser"
                className="text-sm font-bold text-forest-800 hover:text-forest-600 transition-colors cursor-pointer inline-flex items-center gap-1.5"
              >
                <span>Se alle priser</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
            <div className="w-16 h-1 bg-forest-800 rounded-full mt-2"></div>
          </div>

          {/* Feature Grid with Left Borders (NO ICONS, NO CARDS, NO SHADOWS) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
            {SERVICES.map((s, idx) => (
              <div
                key={s.id}
                className="border-l-2 border-forest-800 pl-6 space-y-3 flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-earth-700 block">
                    YDELSE 0{idx + 1}
                  </span>
                  <h3 className="text-xl font-bold uppercase tracking-wide text-forest-900">
                    {s.title}
                  </h3>
                  <p className="text-sm text-charcoal-700 leading-relaxed font-normal">
                    {s.short}
                  </p>
                </div>
                
                <div className="pt-3">
                  <Link
                    to={s.link}
                    className="text-xs font-bold uppercase tracking-widest text-forest-800 hover:text-forest-600 cursor-pointer inline-flex items-center gap-2 transition-colors"
                  >
                    <span>Læs detaljer</span>
                    <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CMS GALLERI PREVIEW */}
      <section className="py-12 bg-sand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CMSGallery gallerySlug="forside" title="Billedgalleri fra hverdagen" />
        </div>
      </section>

      {/* FAQ PREVIEW - SPLIT LAYOUT WITH ACCORDIONS AND RIGHT SIDE CARDS */}
      <section className="py-20 bg-sand-50 border-t border-sand-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-14 space-y-2">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-earth-700">
              <i className="fa-solid fa-circle-question text-emerald-700"></i>
              <span>Ofte stillede spørgsmål</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-forest-900 tracking-tight">
              FAQ-preview
            </h2>
            <p className="text-charcoal-700 text-sm sm:text-base">
              Få hurtige svar på de mest almindelige spørgsmål om naturlig hovtrimning og barfodsheste.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Left Column (2/3 width): Vertical Stack of Accordions */}
            <div className="lg:col-span-8 space-y-4">
              {FAQS.slice(0, 5).map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? "bg-forest-900 text-white border-forest-800 shadow-md"
                        : "bg-white text-forest-900 border-sand-300/80 hover:border-sand-400 shadow-xs"
                    }`}
                  >
                    <button
                      onClick={() => toggleFaq(idx)}
                      type="button"
                      className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 cursor-pointer focus:outline-none"
                    >
                      <span className={`font-bold text-base sm:text-lg leading-snug ${isOpen ? "text-white" : "text-forest-900"}`}>
                        {faq.question}
                      </span>
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 ${
                          isOpen ? "bg-forest-800 text-emerald-400 rotate-180" : "bg-sand-100 text-forest-800"
                        }`}
                      >
                        <i className="fa-solid fa-chevron-down text-xs"></i>
                      </div>
                    </button>

                    <div
                      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                        isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="px-5 pb-6 sm:px-6 sm:pb-6 pt-0 text-sm text-sand-200/90 leading-relaxed border-t border-forest-800/60 mt-1">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="pt-2">
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-sm shadow-sm transition-colors cursor-pointer"
                >
                  <span>Se alle 30 spørgsmål & svar på FAQ-siden</span>
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </div>

            {/* Right Column (1/3 width): 2 Stacked Cards */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Top Card: Solid Primary Block */}
              <div className="bg-forest-900 text-white p-6 sm:p-8 rounded-2xl border border-forest-800 shadow-md space-y-4">
                <div className="w-12 h-12 rounded-xl bg-forest-800 text-emerald-400 flex items-center justify-center text-xl font-bold">
                  <i className="fa-solid fa-comments"></i>
                </div>
                <h3 className="text-xl font-bold text-white">Har du flere spørgsmål?</h3>
                <p className="text-xs sm:text-sm text-sand-200 leading-relaxed">
                  Har du spørgsmål om hestens overgang fra sko til barfodet eller din hests specifikke behov, er du altid velkommen til at kontakte mig.
                </p>
                <Link
                  to="/kontakt"
                  className="block w-full text-center px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-forest-950 font-extrabold text-sm transition-colors shadow-xs cursor-pointer"
                >
                  Kontakt Metteline
                </Link>
              </div>

              {/* Bottom Card: Lighter Surface Card */}
              <div className="bg-white p-6 rounded-2xl border border-sand-300/80 shadow-xs space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-forest-100 text-forest-800 flex items-center justify-center text-lg">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-earth-700 block">
                      Direkte kontakt
                    </span>
                    <span className="font-bold text-forest-900 text-sm">
                      {COMPANY.phone}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-charcoal-600 leading-relaxed">
                  Ring eller send en SMS, hvis du ønsker at høre mere om kørsel på næste rute i dit område.
                </p>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* KONTAKT CTA - FULL-WIDTH CENTERED BANNER WITH ROUNDED CORNERS & MARGINS */}
      <section className="py-12 bg-sand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-forest-950 text-white py-16 sm:py-20 px-6 sm:px-12 text-center shadow-2xl border border-forest-800">
            
            {/* Background Image Overlay */}
            <div className="absolute inset-0 z-0 opacity-60">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,format=auto,quality=80/assets/811d3c72-b547-489f-a70d-a64fabca8eaa/image00011.jpeg"
                alt="Hoofcare baggrund"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-forest-950/40"></div>
            </div>

            <div className="relative z-10 max-w-3xl mx-auto space-y-6">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-emerald-300 block">
                Udkørende hovtrimmer i hele Jylland
              </span>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                Har din hest brug for skånsom hovtrimning?
              </h2>

              <p className="text-sand-200 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                Jeg kører i hele Jylland. Kontakt mig i dag for en uforpligtende snak om din hest eller for at booke tid på næste samlerute.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/kontakt"
                  className="rounded-full bg-forest-700 hover:bg-forest-600 text-white px-9 py-4 font-bold text-base transition-colors shadow-lg cursor-pointer inline-flex items-center gap-2.5"
                >
                  <i className="fa-solid fa-envelope text-emerald-300"></i>
                  <span>Gå til kontaktside</span>
                </Link>

                <a
                  href={COMPANY.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-blue-900/60 hover:bg-blue-900/80 text-white border border-blue-700/60 px-8 py-4 font-semibold text-base transition-colors cursor-pointer inline-flex items-center gap-2"
                >
                  <i className="fa-brands fa-facebook text-xl text-blue-300"></i>
                  <span>Følg os på Facebook</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
